import { sendGTMEvent } from '@next/third-parties/google';
import {
  FormattedTrackingProperties,
  TrackingEventItem,
  TrackingEventName,
  TrackingEventProperties,
  TrackingItem,
} from '@/src/lib/tracking/types';
import { AbstractProduct, ConcreteProduct, DeserializedAbstractProduct } from '@/src/lib/product/types';
import { DeserializedCartItem } from '@/src/lib/cart/glue/types/deserialized';
import { calculateFullScalePrice } from '@/src/lib/product/business/product-prices';
import {
  getProductPriceFromItem,
  isAbstractProduct,
  isCartItem,
  isConcreteProduct,
  isDeserializedAbstractProductWithConcreteProduct,
  isExtendedProductListItem,
} from '@/src/lib/product/business/product-item';
import { DeserializedConcreteProduct, ExtendedProductListItem } from '@/src/lib/catalog-search/types';

function clearAbstractPrefixFromSku(sku: string): string {
  return sku.replace('abstract-', '');
}

function mapTrackingPropertiesFromConcreteProduct(
  product: (ConcreteProduct & { quantity?: number }) | DeserializedConcreteProduct,
): TrackingItem {
  return {
    item_id: clearAbstractPrefixFromSku(product.sku),
    item_name: product.name,
    price: calculateFullScalePrice(product.concreteProductPrices ? (product.concreteProductPrices[0].price ?? 0) : 0),
    quantity: product.quantity,
  };
}
function mapTrackingPropertiesFromAbstractProduct(product: AbstractProduct & { quantity?: number }): TrackingItem {
  return {
    item_id: clearAbstractPrefixFromSku(product.abstractSku),
    item_name: product.abstractName,
    price: calculateFullScalePrice(product.price),
    quantity: product.quantity,
  };
}

function mapTrackingPropertiesFromCartItem(product: DeserializedCartItem & { quantity?: number }): TrackingItem {
  return {
    item_id: clearAbstractPrefixFromSku(product.abstractSku),
    item_name: product.name,
    price: calculateFullScalePrice(product.unitPrice),
    quantity: product.quantity,
  };
}

function mapTrackingPropertiesFromDeserializedAbstractProduct(
  product: DeserializedAbstractProduct & { quantity?: number },
): TrackingItem {
  const price = calculateFullScalePrice(getProductPriceFromItem(product));

  const itemCategory = product.categoryNodes?.[0]?.parents?.length ? product.categoryNodes?.[0]?.name : undefined;
  const itemCategory2 = product.categoryNodes?.[0]?.parents?.[0].parents?.length
    ? product.categoryNodes?.[0]?.parents?.[0]?.name
    : undefined;

  return {
    item_id: clearAbstractPrefixFromSku(product.sku),
    item_name: product.name,
    item_category: itemCategory,
    item_category2: itemCategory2,
    price,
    quantity: product.quantity,
  };
}

function mapTrackingPropertiesFromExtendedProductListItem(
  product: ExtendedProductListItem & { quantity?: number },
): TrackingItem {
  return {
    item_id: clearAbstractPrefixFromSku(product.abstractSku),
    item_name: product.abstractName,
    price: calculateFullScalePrice(product.price),
    quantity: product.quantity,
  };
}

export function mapTrackingPropertiesFromItem(item: TrackingEventItem): TrackingItem {
  if (isExtendedProductListItem(item)) {
    return mapTrackingPropertiesFromExtendedProductListItem(item);
  }

  if (isDeserializedAbstractProductWithConcreteProduct(item)) {
    return mapTrackingPropertiesFromDeserializedAbstractProduct(item);
  }

  if (isAbstractProduct(item)) {
    return mapTrackingPropertiesFromAbstractProduct(item);
  }

  if (isConcreteProduct(item)) {
    return mapTrackingPropertiesFromConcreteProduct(item);
  }

  if (isCartItem(item)) {
    return mapTrackingPropertiesFromCartItem(item);
  }

  return mapTrackingPropertiesFromDeserializedAbstractProduct(item);
}

export function generateTrackingPropertiesFromGenericData({
  currency,
  value,
  items,
  ...otherProps
}: TrackingEventProperties): FormattedTrackingProperties {
  return {
    currency: currency,
    value: value,
    items: items.map(mapTrackingPropertiesFromItem),
    ...otherProps,
  };
}

export function sendTrackingEvent(eventName: TrackingEventName, eventProperties: TrackingEventProperties): void {
  const formattedEventProperties = generateTrackingPropertiesFromGenericData(eventProperties);

  // eslint-disable-next-line no-console
  console.log({ event: eventName, value: formattedEventProperties });
  sendGTMEvent({ event: eventName, value: formattedEventProperties });
}
